import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import EntityCardActions from '../components/EntityCardActions';
import ContactLinksDialog, { EntityMultiSelect } from '../components/ContactLinksDialog';
import useFavorites from '../hooks/useFavorites';
import useListStateCache from '../hooks/useListStateCache';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePlanGate, planGateFromResponse } from '../lib/planGate';
import PlanLimitDialog from '../components/dialogs/PlanLimitDialog';
import { toast } from 'sonner';
import { STANDARD_INDUSTRIES, getIndustryLabel } from '../constants/industries';
import { getCountryOptions, getCountryName } from '../constants/countries';
import IndustryMultiSelectFilter from '../components/IndustryMultiSelectFilter';
import { OnboardingEmptyState } from '../onboarding';
import { 
  UserCircle, Search, Globe, Building, Building2, Briefcase, 
  ChevronDown, X, Factory, Mail, Phone, Linkedin,
  RefreshCw, MapPin, Star, ArrowRight, Users, ArrowLeft,
  Download, FileJson, FileSpreadsheet, FileText, Plus, Save, Loader2, Heart, LayoutGrid, List,
  ShieldOff
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const countryFlags = {
  SA: '🇸🇦', AE: '🇦🇪', OM: '🇴🇲', KW: '🇰🇼', BH: '🇧🇭', QA: '🇶🇦'
};

const countryNames = {
  en: { SA: 'Saudi Arabia', AE: 'UAE', OM: 'Oman', KW: 'Kuwait', BH: 'Bahrain', QA: 'Qatar' },
  ar: { SA: 'السعودية', AE: 'الإمارات', OM: 'عمان', KW: 'الكويت', BH: 'البحرين', QA: 'قطر' }
};

const positionColors = {
  ceo: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
  director: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  manager: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  engineer: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  consultant: 'bg-pink-500/10 text-pink-500 border-pink-500/30',
  analyst: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/30'
};

// WO-21 — the MOCK_CONTACTS array that used to sit here (ten invented
// people with invented companies, emails and phone numbers) was shown to
// the customer whenever the contacts API call failed. Deleted: a CRM that
// invents contacts is worse than a CRM that says it could not load.

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Multi-select filter component
const MultiSelectFilter = ({ label, icon: Icon, options, selected, onChange, language, isRTL, testId, searchable = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  
  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const clearAll = () => onChange([]);
  const filteredOptions = searchable && query
    ? options.filter(o => (o.label || '').toLowerCase().includes(query.toLowerCase()))
    : options;
  const selectAll = () => onChange(filteredOptions.map(o => o.value));

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(''); }}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className="h-11 px-3 justify-between min-w-[160px]"
          data-testid={testId}
        >
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <span className="truncate max-w-[100px]">
              {selected.length === 0 
                ? label 
                : selected.length === 1 
                  ? options.find(o => o.value === selected[0])?.label || selected[0]
                  : `${selected.length} ${language === 'ar' ? 'محدد' : 'selected'}`
              }
            </span>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        {searchable && (
          <div className="p-2 border-b">
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={language === 'ar' ? 'بحث...' : 'Search...'}
              className="h-9 bg-background"
              data-testid={`${testId}-search`}
            />
          </div>
        )}
        <div className="p-2 border-b flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={selectAll} className="h-7 text-xs">
            {language === 'ar' ? 'تحديد الكل' : 'Select All'}
          </Button>
          <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 text-xs">
            {language === 'ar' ? 'مسح' : 'Clear'}
          </Button>
        </div>
        <ScrollArea className="h-[250px]">
          <div className="p-2 space-y-1">
            {filteredOptions.length === 0 ? (
              <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                {language === 'ar' ? 'لا توجد نتائج' : 'No results'}
              </div>
            ) : filteredOptions.map((option) => (
              <div
                key={option.value}
                className={`flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer hover:bg-muted transition-colors ${
                  selected.includes(option.value) ? 'bg-primary/10' : ''
                }`}
                onClick={() => toggleOption(option.value)}
              >
                <Checkbox
                  checked={selected.includes(option.value)}
                  onCheckedChange={() => toggleOption(option.value)}
                  className="pointer-events-none"
                />
                {option.icon && <span>{option.icon}</span>}
                <span className="text-sm flex-1">{option.label}</span>
              </div>
            ))}
          </div>
        </ScrollArea>
        {selected.length > 0 && (
          <div className="p-2 border-t">
            <div className="flex flex-wrap gap-1">
              {selected.slice(0, 3).map((val) => {
                const opt = options.find(o => o.value === val);
                return (
                  <Badge key={val} variant="secondary" className="text-xs">
                    {opt?.icon} {opt?.label?.slice(0, 10)}
                    <X 
                      className="w-3 h-3 ml-1 cursor-pointer" 
                      onClick={(e) => { e.stopPropagation(); toggleOption(val); }}
                    />
                  </Badge>
                );
              })}
              {selected.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{selected.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default function ContactsPage() {
  const { language, isRTL } = useLanguage();
  const { getToken, user, token } = useAuth();
  const planGate = usePlanGate();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [deletingContactId, setDeletingContactId] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);
  
  // Add Contact Dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('contacts_view_mode') || 'card');
  useEffect(() => { localStorage.setItem('contacts_view_mode', viewMode); }, [viewMode]);
  const [isCreating, setIsCreating] = useState(false);
  const [newContactForm, setNewContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    company: '',
    country: 'SA',
    city: '',
    linkedin: '',
    notes: '',
    linked_company_ids: [],
    linked_project_ids: []
  });
  
  // Linkable entities (companies & projects) for linking + filtering
  const [linkOptions, setLinkOptions] = useState({ companies: [], projects: [] });
  // Manage Links dialog
  const [linksContact, setLinksContact] = useState(null);
  const [showLinksDialog, setShowLinksDialog] = useState(false);
  
  // Filters
  const [search, setSearch] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState([]); // legacy position_type (kept for mock data)
  const [positionSearch, setPositionSearch] = useState('');
  const [positionInput, setPositionInput] = useState('');
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  
  // Use the centralized favorites hook
  const { 
    isFavorite, 
    handleFavoriteToggle, 
    filterByFavorites,
    showFavoritesOnly, 
    setShowFavoritesOnly 
  } = useFavorites('contact', token);
  
  const PAGE_SIZE = 12;

  // Check if user can export (not free plan)
  const canExport = user?.plan_id?.toLowerCase() !== 'free';

  // Create new contact
  const handleCreateContact = async () => {
    if (!newContactForm.name.trim()) {
      toast.error(language === 'ar' ? 'الاسم مطلوب' : 'Name is required');
      return;
    }
    
    setIsCreating(true);
    try {
      const token = await getToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newContactForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(language === 'ar' ? 'تم إنشاء جهة الاتصال بنجاح' : 'Contact created successfully');
        setShowAddDialog(false);
        setNewContactForm({
          name: '',
          email: '',
          phone: '',
          position: '',
          company: '',
          country: 'SA',
          city: '',
          linkedin: '',
          notes: '',
          linked_company_ids: [],
          linked_project_ids: []
        });
        fetchContacts(); // Refresh list
      } else {
        const pg = await planGateFromResponse(response);
        if (planGate.show(pg)) return;
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to create contact');
      }
    } catch (error) {
      console.error('Error creating contact:', error);
      toast.error(language === 'ar' ? 'فشل في إنشاء جهة الاتصال' : 'Failed to create contact');
    } finally {
      setIsCreating(false);
    }
  };

  // Export handler — the server returns a structured 402 plan_gate when the
  // plan is too low; surface the shared upgrade dialog instead of a raw error.
  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const tok = getToken();
      const response = await fetch(`${API_URL}/api/contacts/export?format=${format}`, {
        headers: {
          'Authorization': `Bearer ${tok}`
        }
      });

      if (!response.ok) {
        const pg = await planGateFromResponse(response);
        if (planGate.show(pg)) return;
        let detail = '';
        try { detail = (await response.clone().json()).detail; } catch { /* non-json */ }
        throw new Error(detail || (language === 'ar' ? 'تعذّر التصدير' : 'Export could not be completed'));
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `contacts_export.${format === 'excel' ? 'xlsx' : format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=([^;]+)/);
        if (match) filename = match[1].replace(/"/g, '');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(language === 'ar' 
        ? `تم تصدير جهات الاتصال بنجاح (${format.toUpperCase()})`
        : `Contacts exported successfully (${format.toUpperCase()})`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || (language === 'ar' ? 'فشل التصدير' : 'Export failed'));
    } finally {
      setIsExporting(false);
    }
  };

  // BUG-UI02 FIX: delete contact (admin only)
  const deleteContact = async (contactId) => {
    // BUG-04: confirmation is handled by the Radix AlertDialog (contactToDelete).
    // The native window.confirm() was a duplicate, inconsistent prompt — removed.
    setDeletingContactId(contactId);
    try {
      const tok = getToken();
      const response = await fetch(`${API_URL}/api/contacts/${contactId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${tok}` }
      });
      if (!response.ok) throw new Error('Failed to delete contact');
      toast.success('Contact deleted');
      setContacts(prev => prev.filter(c => c._id !== contactId));
      setTotal(prev => prev - 1);
    } catch (err) {
      toast.error(err.message || 'Failed to delete contact');
    } finally {
      setDeletingContactId(null);
      setContactToDelete(null);
    }
  };

  const fetchContacts = async () => {
    setLoading(true);
    
    try {
      const token = getToken();
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', PAGE_SIZE);
      
      if (search) params.append('search', search);
      if (selectedCountries.length === 1) params.append('country', selectedCountries[0]);
      if (selectedPositions.length === 1) params.append('position_type', selectedPositions[0]);
      if (positionSearch) params.append('position', positionSearch);
      if (selectedIndustries.length === 1) params.append('industry', selectedIndustries[0]);
      if (selectedCompanies.length === 1) params.append('company_id', selectedCompanies[0]);
      if (selectedProjects.length === 1) params.append('project_id', selectedProjects[0]);
      if (showFavoritesOnly) params.append('favorites_only', 'true');
      
      const response = await fetch(`${API_URL}/api/contacts?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch contacts');
      }
      
      const data = await response.json();
      
      // Map API response to frontend format
      let mappedContacts = data.items.map(item => ({
        _id: item._id,
        ref_id: item.ref_id,
        name: item.name,
        position: item.position,
        positionType: item.position_type,
        company: item.company || item.source_lead_name,
        country: item.country,
        city: item.city,
        email: item.email,
        phone: item.phone,
        linkedin: item.linkedin,
        industries: item.industries || [],
        isFavorite: item.is_favorite,
        lastContact: item.last_contact,
        source: item.source,
        sourceId: item.source_id,
        sourceLeadName: item.source_lead_name,
        user_id: item.user_id,
        // WO-21 — the server masks a contact that belongs to another person and
        // marks it with _is_masked. Without carrying that flag the page shows
        // "A**** A*****" with no explanation, which reads like broken data.
        isMasked: item._is_masked === true,
        // WO-21 — who created it, and whether this person may delete it. Both
        // come from the server: the page must not guess, because the rule now
        // includes "the Team Admin may delete a Team member's entry".
        ownerName: item.owner_name || null,
        isOwn: item.is_own === true,
        canDelete: item.can_delete === true,
        linkedCompanyIds: item.linked_company_ids || [],
        linkedProjectIds: item.linked_project_ids || []
      }));
      
      // Apply client-side filtering for multi-select (API only supports single values)
      if (selectedCountries.length > 1) {
        mappedContacts = mappedContacts.filter(c => selectedCountries.includes(c.country));
      }
      if (selectedPositions.length > 1) {
        mappedContacts = mappedContacts.filter(c => selectedPositions.includes(c.positionType));
      }
      if (selectedIndustries.length > 1) {
        mappedContacts = mappedContacts.filter(c => 
          c.industries?.some(ind => selectedIndustries.includes(ind))
        );
      }
      if (selectedCompanies.length >= 1) {
        mappedContacts = mappedContacts.filter(c => 
          c.linkedCompanyIds?.some(id => selectedCompanies.includes(id))
        );
      }
      if (selectedProjects.length >= 1) {
        mappedContacts = mappedContacts.filter(c => 
          c.linkedProjectIds?.some(id => selectedProjects.includes(id))
        );
      }
      
      setContacts(mappedContacts);
      setTotal(data.total);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      // WO-21 — say the list could not be loaded. It used to fall back to ten
      // invented contacts, so a customer whose CRM failed to load was shown
      // strangers' names and phone numbers as if they were their own.
      setContacts([]);
      setTotal(0);
      toast.error(language === 'ar'
        ? 'تعذر تحميل جهات الاتصال. يرجى المحاولة مرة أخرى.'
        : 'Could not load your contacts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (lastLoadedSigRef.current === fetchSig) return; // already loaded (e.g. just restored)
    lastLoadedSigRef.current = fetchSig;
    fetchContacts();
  }, [selectedCountries, selectedPositions, positionSearch, selectedIndustries, selectedCompanies, selectedProjects, search, showFavoritesOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch linkable entities (companies & projects) once
  useEffect(() => {
    const fetchLinkOptions = async () => {
      try {
        const token = getToken();
        const res = await fetch(`${API_URL}/api/contacts/linkable-entities`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setLinkOptions({ companies: data.companies || [], projects: data.projects || [] });
        }
      } catch (e) {
        console.error('Failed to fetch linkable entities:', e);
      }
    };
    fetchLinkOptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const clearAllFilters = () => {
    setSelectedCountries([]);
    setSelectedPositions([]);
    setPositionSearch('');
    setPositionInput('');
    setSelectedIndustries([]);
    setSelectedCompanies([]);
    setSelectedProjects([]);
    setShowFavoritesOnly(false);
  };

  const hasActiveFilters = selectedCountries.length > 0 || selectedPositions.length > 0 || !!positionSearch || selectedIndustries.length > 0 || selectedCompanies.length > 0 || selectedProjects.length > 0 || showFavoritesOnly;

  // ── Restore loaded cards + filters + scroll when returning from a detail page ──
  const { restoredState, write } = useListStateCache('contacts', '/dashboard/contacts/');
  const fetchSig = JSON.stringify({
    search, selectedCountries, selectedPositions, positionSearch,
    selectedIndustries, selectedCompanies, selectedProjects, showFavoritesOnly,
  });
  const lastLoadedSigRef = useRef(restoredState ? restoredState.sig : null);

  useLayoutEffect(() => {
    if (!restoredState) return;
    const f = restoredState.filters || {};
    setSearch(f.search || '');
    setSearchInput(f.searchInput || '');
    setSelectedCountries(f.selectedCountries || []);
    setSelectedPositions(f.selectedPositions || []);
    setPositionSearch(f.positionSearch || '');
    setPositionInput(f.positionInput || '');
    setSelectedIndustries(f.selectedIndustries || []);
    setSelectedCompanies(f.selectedCompanies || []);
    setSelectedProjects(f.selectedProjects || []);
    if (f.showFavoritesOnly) setShowFavoritesOnly(true);
    setContacts(restoredState.items || []);
    setTotal(restoredState.total || 0);
    setLoading(false);
    const y = restoredState.scrollY || 0;
    setTimeout(() => window.scrollTo(0, y), 60);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const latestStateRef = useRef({});
  latestStateRef.current = {
    items: contacts, total, sig: fetchSig, scrollY: 0,
    filters: {
      search, searchInput, selectedCountries, selectedPositions, positionSearch, positionInput,
      selectedIndustries, selectedCompanies, selectedProjects, showFavoritesOnly,
    },
  };
  // Persist on every change so returning from a detail page restores everything.
  useEffect(() => {
    write({ ...latestStateRef.current, scrollY: window.scrollY });
  }, [contacts, total, fetchSig, showFavoritesOnly, write]);

  // Distinct positions from loaded contacts (suggestions for the typeable filter)
  const positionSuggestions = Array.from(
    new Set((contacts || []).map(c => c.position).filter(Boolean))
  ).sort();

  const openLinksDialog = (contact) => {
    setLinksContact({
      _id: contact._id,
      name: contact.name,
      linked_company_ids: contact.linkedCompanyIds || [],
      linked_project_ids: contact.linkedProjectIds || []
    });
    setShowLinksDialog(true);
  };

  const getInitials = (name) => {
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  const translations = {
    en: {
      title: 'Contacts',
      subtitle: 'Your network of key decision-makers in GCC construction',
      searchPlaceholder: 'Search contacts...',
      allCountries: 'All Countries',
      allPositions: 'All Positions',
      allIndustries: 'All Industries',
      ceo: 'CEO',
      director: 'Director',
      manager: 'Manager',
      engineer: 'Engineer',
      consultant: 'Consultant',
      analyst: 'Analyst',
      results: 'contacts found',
      noContacts: 'No contacts found',
      noContactsDesc: 'Try adjusting your filters or add new contacts',
      viewProfile: 'View Profile',
      sendEmail: 'Send Email',
      call: 'Call',
      refresh: 'Refresh',
      activeFilters: 'Active filters:',
      clearAll: 'Clear all',
      favorites: 'Favorites',
      lastContact: 'Last contact',
      addContact: 'Add Contact'
    },
    ar: {
      title: 'جهات الاتصال',
      subtitle: 'شبكتك من صناع القرار الرئيسيين في قطاع البناء الخليجي',
      searchPlaceholder: 'البحث في جهات الاتصال...',
      allCountries: 'جميع الدول',
      allPositions: 'جميع المناصب',
      allIndustries: 'جميع القطاعات',
      ceo: 'الرئيس التنفيذي',
      director: 'مدير',
      manager: 'مدير',
      engineer: 'مهندس',
      consultant: 'استشاري',
      analyst: 'محلل',
      results: 'جهة اتصال',
      noContacts: 'لا توجد جهات اتصال',
      noContactsDesc: 'حاول تعديل عوامل التصفية أو أضف جهات اتصال جديدة',
      viewProfile: 'عرض الملف',
      sendEmail: 'إرسال بريد',
      call: 'اتصال',
      refresh: 'تحديث',
      activeFilters: 'الفلاتر النشطة:',
      clearAll: 'مسح الكل',
      favorites: 'المفضلة',
      lastContact: 'آخر تواصل',
      addContact: 'إضافة جهة اتصال'
    }
  };

  const txt = translations[language];
  
  const positionOptions = [
    { value: 'ceo', label: txt.ceo },
    { value: 'director', label: txt.director },
    { value: 'manager', label: txt.manager },
    { value: 'engineer', label: txt.engineer },
    { value: 'consultant', label: txt.consultant },
    { value: 'analyst', label: txt.analyst }
  ];

  const ContactCard = ({ contact, horizontal = false }) => {
    return (
      <Card 
        className="group border-0 bg-card/50 backdrop-blur-sm hover:shadow-xl transition-all duration-300 cursor-pointer h-full flex flex-col"
        data-testid={`contact-card-${contact._id}`}
        onClick={() => navigate(`/dashboard/contacts/${contact._id}`)}
      >
        <CardContent className="p-6 flex-1 flex flex-col">
          {/* Ref ID Badge */}
          {contact.ref_id && (
            <div className="mb-2">
              <Badge className="bg-primary text-primary-foreground font-mono text-xs" data-testid={`contact-ref-id-${contact._id}`}>
                {contact.ref_id}
              </Badge>
            </div>
          )}
          
          {/* Header with Avatar */}
          <div className={`flex items-start gap-4 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Avatar className="w-14 h-14 ring-2 ring-primary/20">
              <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold text-lg">
                {getInitials(contact.name)}
              </AvatarFallback>
            </Avatar>
            <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : ''}`}>
              <h3 className="text-lg font-bold truncate group-hover:text-primary transition-colors">
                {contact.name}
              </h3>
              <p className="text-sm text-muted-foreground truncate">{contact.position}</p>
              {/* WO-21 — this contact belongs to another person in the company.
                  The server deliberately hides the details; say so, because
                  otherwise the asterisks look like corrupted records. */}
              {contact.isMasked && (
                <Badge
                  variant="outline"
                  className="mt-1 gap-1 text-xs border-amber-500/40 text-amber-600 dark:text-amber-400"
                  data-testid={`contact-masked-${contact._id}`}
                >
                  <ShieldOff className="w-3 h-3" />
                  {language === 'ar' ? 'مخفي للخصوصية' : 'Hidden for privacy'}
                </Badge>
              )}
              {/* WO-21 — somebody else created this one. Say who, which is the
                  condition Lucas set for letting a company's admin read the
                  whole CRM, and which a Team member needs anyway to know that
                  the entry is not theirs to delete. */}
              {!contact.isOwn && contact.ownerName && (
                <p
                  className="text-xs text-muted-foreground mt-1 truncate"
                  data-testid={`contact-owner-${contact._id}`}
                >
                  {language === 'ar' ? 'أنشأها' : 'Created by'} {contact.ownerName}
                </p>
              )}
            </div>
          </div>

          {/* Company */}
          <div className={`flex items-center gap-2 mb-3 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Building className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="truncate font-medium">{contact.company}</span>
          </div>

          {/* Linked Companies & Projects */}
          {((contact.linkedCompanyIds?.length || 0) > 0 || (contact.linkedProjectIds?.length || 0) > 0) && (
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`contact-links-${contact._id}`}>
              {contact.linkedCompanyIds?.slice(0, 2).map(id => (
                <Badge key={`c-${id}`} variant="outline" className="gap-1 text-xs border-blue-500/30 text-blue-600 dark:text-blue-400">
                  <Building2 className="w-3 h-3" />
                  <span className="truncate max-w-[120px]">{linkOptions.companies.find(c => c.id === id)?.name || '—'}</span>
                </Badge>
              ))}
              {(contact.linkedCompanyIds?.length || 0) > 2 && (
                <Badge variant="outline" className="text-xs">+{contact.linkedCompanyIds.length - 2}</Badge>
              )}
              {contact.linkedProjectIds?.slice(0, 2).map(id => (
                <Badge key={`p-${id}`} variant="outline" className="gap-1 text-xs border-purple-500/30 text-purple-600 dark:text-purple-400">
                  <Briefcase className="w-3 h-3" />
                  <span className="truncate max-w-[120px]">{linkOptions.projects.find(p => p.id === id)?.name || '—'}</span>
                </Badge>
              ))}
              {(contact.linkedProjectIds?.length || 0) > 2 && (
                <Badge variant="outline" className="text-xs">+{contact.linkedProjectIds.length - 2}</Badge>
              )}
            </div>
          )}

          {/* Location */}
          <div className={`flex items-center gap-2 mb-4 text-sm text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
            <MapPin className="w-4 h-4 shrink-0" />
            <span>{countryFlags[contact.country] || ''} {contact.city ? `${contact.city}, ` : ''}{getCountryName(contact.country, language)}</span>
          </div>

          {/* Position Badge */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge className={`${positionColors[contact.positionType] || 'bg-gray-500/10'} capitalize`}>
              {txt[contact.positionType] || contact.positionType}
            </Badge>
            {contact.industries?.slice(0, 2).map((ind, i) => (
              <Badge key={i} variant="outline" className="text-xs">
                {getIndustryLabel(ind, language)}
              </Badge>
            ))}
          </div>

          {/* Contact Actions */}
          <div className={`gap-2 mb-4 ${horizontal ? 'flex' : 'grid grid-cols-3'}`}>
            <Button 
              variant="outline" 
              size="sm"
              className="h-9"
              onClick={(e) => { 
                e.stopPropagation(); 
                if (contact.email) {
                  window.location.href = `mailto:${contact.email}`;
                } else {
                  toast.error('No email available');
                }
              }}
              disabled={!contact.email || contact.isMasked}
              data-testid={`email-${contact._id}`}
            >
              <Mail className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              className="h-9"
              onClick={(e) => { 
                e.stopPropagation(); 
                if (contact.phone) {
                  // Clean phone number (remove spaces, dashes, etc) for WhatsApp
                  const cleanPhone = contact.phone.replace(/[\s\-\(\)]/g, '').replace(/^\+/, '');
                  window.open(`https://wa.me/${cleanPhone}`, '_blank');
                } else {
                  toast.error('No phone number available');
                }
              }}
              disabled={!contact.phone || contact.isMasked}
              data-testid={`phone-${contact._id}`}
            >
              <Phone className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              className="h-9"
              onClick={(e) => { 
                e.stopPropagation(); 
                if (contact.linkedin) {
                  // Ensure proper LinkedIn URL format
                  const linkedinUrl = contact.linkedin.startsWith('http') 
                    ? contact.linkedin 
                    : contact.linkedin.startsWith('linkedin.com') 
                      ? `https://${contact.linkedin}`
                      : `https://linkedin.com/in/${contact.linkedin}`;
                  window.open(linkedinUrl, '_blank');
                } else {
                  toast.error('No LinkedIn profile available');
                }
              }}
              disabled={!contact.linkedin || contact.isMasked}
              data-testid={`linkedin-${contact._id}`}
            >
              <Linkedin className="w-4 h-4" />
            </Button>
          </div>

          {/* Last Contact */}
          <div className={`text-xs text-muted-foreground mb-4 ${isRTL ? 'text-right' : ''}`}>
            {txt.lastContact}: {contact.lastContact
              ? new Date(contact.lastContact).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')
              : (language === 'ar' ? 'غير متاح' : '—')}
          </div>

          {/* Footer */}
          <div className={`flex items-center justify-between pt-4 border-t border-border/50 mt-auto ${isRTL ? 'flex-row-reverse' : ''}`}>
            <EntityCardActions
              itemId={contact._id}
              itemType="contact"
              itemTitle={`${contact.name} - ${contact.position || 'Contact'}`}
              isAdmin={isAdmin}
              canEdit={true}
              canLink={true}
              onLink={() => openLinksDialog(contact)}
              onEdit={() => navigate(`/dashboard/contacts/${contact._id}`)}
              /* WO-21 — the server answers this now (can_delete): creator, or
                 the Team Admin of whoever created it, or the MID2BID operator.
                 The page used to work it out itself and so never offered the
                 button to a Team Admin. */
              onDelete={contact.canDelete ? () => setContactToDelete(contact) : undefined}
              isFavorite={isFavorite(contact._id)}
              onFavoriteToggle={(isFav) => handleFavoriteToggle(contact._id, isFav)}
              token={token}
              language={language}
              isRTL={isRTL}
              primaryAction={() => navigate(`/dashboard/contacts/${contact._id}`)}
              primaryLabel={txt.viewProfile}
              primaryIcon={ArrowRight}
            />
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="contacts-page">
      <PlanLimitDialog
        open={planGate.open}
        onOpenChange={planGate.setOpen}
        gate={planGate.gate}
        message={planGate.message}
      />
      {/* Back to Dashboard Button */}
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        data-testid="back-to-dashboard-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        {language === 'ar' ? 'العودة إلى لوحة التحكم' : 'Back to Dashboard'}
      </Button>

      {/* Header */}
      <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : ''}>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 shadow-lg">
              <UserCircle className="w-7 h-7 text-white" />
            </div>
            {txt.title}
          </h1>
          <p className="text-muted-foreground mt-2">{txt.subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2" data-wo40="header-actions">
          {/* Export Button */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                disabled={isExporting}
                data-testid="export-contacts-btn"
              >
                <Download className="w-4 h-4" />
                {isExporting 
                  ? (language === 'ar' ? 'جاري التصدير...' : 'Exporting...') 
                  : (language === 'ar' ? 'تصدير' : 'Export')}
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('json')} className="cursor-pointer">
                <FileJson className="w-4 h-4 mr-2" />
                JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')} className="cursor-pointer">
                <FileText className="w-4 h-4 mr-2" />
                CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('excel')} className="cursor-pointer">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button 
            variant="outline" 
            onClick={fetchContacts}
            className="flex items-center gap-2"
            data-testid="refresh-contacts-btn"
          >
            <RefreshCw className="w-4 h-4" />
            {txt.refresh}
          </Button>
          {/* View mode toggle */}
          <div className="flex items-center border rounded-md overflow-hidden">
            <Button
              variant={viewMode === 'card' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none h-9 px-3"
              onClick={() => setViewMode('card')}
              data-testid="contacts-view-card-btn"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none h-9 px-3"
              onClick={() => setViewMode('list')}
              data-testid="contacts-view-list-btn"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
          <Button 
            className="flex items-center gap-2 bg-primary hover:bg-primary/90"
            data-testid="add-contact-btn"
            onClick={() => setShowAddDialog(true)}
          >
            <Users className="w-4 h-4" />
            {txt.addContact}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 bg-card/50 backdrop-blur-sm">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className={`flex flex-col md:flex-row gap-3 flex-wrap ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            <div className="relative flex-1 min-w-[200px]">
              <Search className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-5 h-5 text-muted-foreground`} />
              <Input
                type="text"
                placeholder={txt.searchPlaceholder}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={`${isRTL ? 'pr-10' : 'pl-10'} h-11`}
                data-testid="contacts-search-input"
              />
            </div>
            
            {/* Multi-select Country Filter */}
            <MultiSelectFilter
              label={txt.allCountries}
              icon={Globe}
              options={getCountryOptions(language, { gccFirst: true }).map(c => ({
                value: c.code,
                label: c.name,
                icon: countryFlags[c.code]
              }))}
              selected={selectedCountries}
              onChange={(values) => { setSelectedCountries(values); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="contacts-country-filter"
              searchable
            />
            
            {/* Multi-select Industry Filter (grouped by category) */}
            <IndustryMultiSelectFilter
              label={txt.allIndustries}
              icon={Factory}
              selected={selectedIndustries}
              onChange={(values) => { setSelectedIndustries(values); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="contacts-industry-filter"
            />
            
            {/* Project Filter */}
            <MultiSelectFilter
              label={language === 'ar' ? 'جميع المشاريع' : 'All Projects'}
              icon={Briefcase}
              options={linkOptions.projects.map(p => ({ value: p.id, label: p.name }))}
              selected={selectedProjects}
              onChange={(values) => { setSelectedProjects(values); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="contacts-project-filter"
              searchable
            />
            
            {/* Company Filter */}
            <MultiSelectFilter
              label={language === 'ar' ? 'جميع الشركات' : 'All Companies'}
              icon={Building2}
              options={linkOptions.companies.map(c => ({ value: c.id, label: c.name }))}
              selected={selectedCompanies}
              onChange={(values) => { setSelectedCompanies(values); setPage(1); }}
              language={language}
              isRTL={isRTL}
              testId="contacts-company-filter"
              searchable
            />
            
            {/* Position Filter — free-text typeable */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 px-3 justify-between min-w-[160px]"
                  data-testid="contacts-position-filter"
                >
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-muted-foreground" />
                    <span className="truncate max-w-[110px]">
                      {positionSearch || txt.allPositions}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[260px] p-2" align="start">
                <Input
                  autoFocus
                  value={positionInput}
                  onChange={(e) => setPositionInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setPositionSearch(positionInput.trim());
                      setPage(1);
                    }
                  }}
                  placeholder={language === 'ar' ? 'اكتب المنصب...' : 'Type a position...'}
                  className="h-9 bg-background"
                  data-testid="contacts-position-input"
                />
                <div className="flex items-center justify-between mt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => { setPositionInput(''); setPositionSearch(''); setPage(1); }}
                    data-testid="contacts-position-clear"
                  >
                    {language === 'ar' ? 'مسح' : 'Clear'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => { setPositionSearch(positionInput.trim()); setPage(1); }}
                    data-testid="contacts-position-apply"
                  >
                    {language === 'ar' ? 'تطبيق' : 'Apply'}
                  </Button>
                </div>
                {positionSuggestions.filter(p => p.toLowerCase().includes(positionInput.toLowerCase())).length > 0 && (
                  <div className="mt-2 border-t border-border pt-2 max-h-44 overflow-y-auto space-y-0.5">
                    {positionSuggestions
                      .filter(p => p.toLowerCase().includes(positionInput.toLowerCase()))
                      .slice(0, 10)
                      .map(p => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => { setPositionInput(p); setPositionSearch(p); setPage(1); }}
                          className="block w-full text-left text-sm px-2 py-1.5 rounded hover:bg-muted truncate"
                          data-testid={`contacts-position-suggestion-${p}`}
                        >
                          {p}
                        </button>
                      ))}
                  </div>
                )}
              </PopoverContent>
            </Popover>
            
            {/* Favorites Toggle */}
            <Button 
              type="button"
              variant={showFavoritesOnly ? "default" : "outline"}
              className="h-11"
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              data-testid="contacts-favorites-filter"
            >
              <Heart className={`w-4 h-4 mr-2 ${showFavoritesOnly ? 'fill-current' : ''}`} />
              {txt.favorites}
            </Button>
            
            <Button type="submit" className="bg-primary hover:bg-primary/90 h-11" data-testid="contacts-search-btn">
              <Search className="w-4 h-4 mr-2" />
              {language === 'ar' ? 'بحث' : 'Search'}
            </Button>
          </form>
          
          {/* Active filters display */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50 flex-wrap">
              <span className="text-sm text-muted-foreground">{txt.activeFilters}</span>
              {selectedCountries.map(code => (
                <Badge key={code} variant="secondary" className="gap-1">
                  {countryFlags[code] || ''} {getCountryName(code, language)}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedCountries(prev => prev.filter(c => c !== code))} />
                </Badge>
              ))}
              {selectedPositions.map(pos => (
                <Badge key={pos} variant="secondary" className="gap-1">
                  <Briefcase className="w-3 h-3" /> {txt[pos]}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedPositions(prev => prev.filter(p => p !== pos))} />
                </Badge>
              ))}
              {positionSearch && (
                <Badge variant="secondary" className="gap-1">
                  <Briefcase className="w-3 h-3" /> {positionSearch}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => { setPositionSearch(''); setPositionInput(''); }} />
                </Badge>
              )}
              {selectedIndustries.map(industry => (
                <Badge key={industry} variant="secondary" className="gap-1">
                  <Factory className="w-3 h-3" /> {getIndustryLabel(industry, language)}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedIndustries(prev => prev.filter(i => i !== industry))} />
                </Badge>
              ))}
              {selectedCompanies.map(id => (
                <Badge key={id} variant="secondary" className="gap-1">
                  <Building2 className="w-3 h-3" /> {linkOptions.companies.find(c => c.id === id)?.name || id}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedCompanies(prev => prev.filter(c => c !== id))} />
                </Badge>
              ))}
              {selectedProjects.map(id => (
                <Badge key={id} variant="secondary" className="gap-1">
                  <Briefcase className="w-3 h-3" /> {linkOptions.projects.find(p => p.id === id)?.name || id}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedProjects(prev => prev.filter(p => p !== id))} />
                </Badge>
              ))}
              {showFavoritesOnly && (
                <Badge variant="secondary" className="gap-1">
                  <Star className="w-3 h-3 fill-current" /> {txt.favorites}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setShowFavoritesOnly(false)} />
                </Badge>
              )}
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs h-7">
                {txt.clearAll}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results count */}
      {!loading && (
        <div className={`text-sm text-muted-foreground ${isRTL ? 'text-right' : ''}`}>
          {language === 'ar' 
            ? `عرض ${contacts.length} من ${total} جهة اتصال` 
            : `Showing ${contacts.length} of ${total} contacts`
          }
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="border-0 bg-card/50">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start gap-4">
                  <Skeleton className="h-14 w-14 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-6 w-20" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : contacts.length === 0 ? (
        /* WO-33 — helpful empty screen (onboarding switch on); the old card otherwise. */
        <OnboardingEmptyState
          kind={(hasActiveFilters || !!search) ? 'noResults' : 'contacts'}
          to="/dashboard/crm"
          onClear={() => { clearAllFilters(); setSearch(''); setSearchInput(''); }}
        >
        <Card className="border-0 bg-card/50">
          <CardContent className="p-12 text-center">
            <UserCircle className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-xl font-semibold mb-2">{txt.noContacts}</h3>
            <p className="text-muted-foreground">{txt.noContactsDesc}</p>
          </CardContent>
        </Card>
        </OnboardingEmptyState>
      ) : (
        <div className={viewMode === 'list' ? 'flex flex-col gap-4' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr'}>
          {contacts.map((contact) => (
            <ContactCard key={contact._id} contact={contact} horizontal={viewMode === 'list'} />
          ))}
        </div>
      )}

      {/* Add Contact Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Plus className="w-5 h-5 text-primary" />
              {language === 'ar' ? 'إضافة جهة اتصال جديدة' : 'Add New Contact'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Name */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'الاسم' : 'Name'} *</Label>
              <Input 
                value={newContactForm.name} 
                onChange={(e) => setNewContactForm(prev => ({ ...prev, name: e.target.value }))} 
                placeholder={language === 'ar' ? 'أدخل الاسم الكامل' : 'Enter full name'}
                className="bg-background"
              />
            </div>
            
            {/* Email & Phone */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</Label>
                <Input 
                  type="email"
                  value={newContactForm.email} 
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, email: e.target.value }))} 
                  placeholder="email@company.com"
                  className="bg-background"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'الهاتف' : 'Phone'}</Label>
                <Input 
                  value={newContactForm.phone} 
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, phone: e.target.value }))} 
                  placeholder="+966 5XX XXX XXXX"
                  className="bg-background"
                />
              </div>
            </div>
            
            {/* Position & Company */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'المنصب' : 'Position'}</Label>
                <Input 
                  value={newContactForm.position} 
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, position: e.target.value }))} 
                  placeholder={language === 'ar' ? 'المسمى الوظيفي' : 'Job title'}
                  className="bg-background"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'الشركة' : 'Company'}</Label>
                <Input 
                  value={newContactForm.company} 
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, company: e.target.value }))} 
                  placeholder={language === 'ar' ? 'اسم الشركة' : 'Company name'}
                  className="bg-background"
                />
              </div>
            </div>
            
            {/* Country & City */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'الدولة' : 'Country'}</Label>
                <Select 
                  value={newContactForm.country} 
                  onValueChange={(v) => setNewContactForm(prev => ({ ...prev, country: v }))}
                >
                  <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {getCountryOptions(language, { gccFirst: true }).map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        {countryFlags[c.code] ? `${countryFlags[c.code]} ` : ''}{c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'المدينة' : 'City'}</Label>
                <Input 
                  value={newContactForm.city} 
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, city: e.target.value }))} 
                  placeholder={language === 'ar' ? 'اسم المدينة' : 'City name'}
                  className="bg-background"
                />
              </div>
            </div>
            
            {/* LinkedIn */}
            <div className="space-y-2">
              <Label>LinkedIn</Label>
              <Input 
                value={newContactForm.linkedin} 
                onChange={(e) => setNewContactForm(prev => ({ ...prev, linkedin: e.target.value }))} 
                placeholder="https://linkedin.com/in/username"
                className="bg-background"
              />
              {!isAdmin && (
                <p className="text-xs text-muted-foreground" data-testid="linkedin-safe-hint">
                  {language === 'ar' ? 'يجب أن يكون رابطًا آمنًا (https). يتم فحصه ضد المواقع الضارة.' : 'Must be a safe https link — checked against malicious sites.'}
                </p>
              )}
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea 
                value={newContactForm.notes} 
                onChange={(e) => setNewContactForm(prev => ({ ...prev, notes: e.target.value }))} 
                placeholder={language === 'ar' ? 'ملاحظات إضافية...' : 'Additional notes...'}
                rows={3}
                className="bg-background"
              />
              {!isAdmin && (
                <p className="text-xs text-muted-foreground" data-testid="notes-no-links-hint">
                  {language === 'ar' ? 'لا يُسمح بالروابط في هذا الحقل.' : 'Links/URLs are not allowed in this field.'}
                </p>
              )}
            </div>
            
            {/* Linked Companies */}
            <EntityMultiSelect
              label={language === 'ar' ? 'الشركات المرتبطة' : 'Linked Companies'}
              icon={Building2}
              options={linkOptions.companies}
              selected={newContactForm.linked_company_ids}
              onChange={(ids) => setNewContactForm(prev => ({ ...prev, linked_company_ids: ids }))}
              language={language}
              placeholder={language === 'ar' ? 'اختر الشركات' : 'Select companies'}
              testId="new-contact-companies"
            />
            
            {/* Linked Projects */}
            <EntityMultiSelect
              label={language === 'ar' ? 'المشاريع المرتبطة' : 'Linked Projects'}
              icon={Briefcase}
              options={linkOptions.projects}
              selected={newContactForm.linked_project_ids}
              onChange={(ids) => setNewContactForm(prev => ({ ...prev, linked_project_ids: ids }))}
              language={language}
              placeholder={language === 'ar' ? 'اختر المشاريع' : 'Select projects'}
              testId="new-contact-projects"
            />
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreateContact} 
              disabled={isCreating || !newContactForm.name.trim()}
              className="flex items-center gap-2"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {language === 'ar' ? 'جاري الإنشاء...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {language === 'ar' ? 'إنشاء جهة اتصال' : 'Create Contact'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Links Dialog */}
      <ContactLinksDialog
        open={showLinksDialog}
        onOpenChange={setShowLinksDialog}
        contact={linksContact}
        companies={linkOptions.companies}
        projects={linkOptions.projects}
        token={token}
        language={language}
        isRTL={isRTL}
        onSaved={() => fetchContacts()}
        onPlanGate={(pg) => planGate.show(pg)}
      />

      {/* Delete Contact Confirmation (NEW-6: app modal, not native window.confirm) */}
      <AlertDialog open={!!contactToDelete} onOpenChange={(open) => !open && setContactToDelete(null)}>
        <AlertDialogContent dir={isRTL ? 'rtl' : 'ltr'} data-testid="delete-contact-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{language === 'ar' ? 'حذف جهة الاتصال' : 'Delete Contact'}</AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'ar'
                ? 'هل أنت متأكد من حذف جهة الاتصال هذه؟ لا يمكن التراجع عن هذا الإجراء.'
                : 'Are you sure you want to delete this contact? This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {contactToDelete && (
            <div className="my-2 p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium line-clamp-2">{contactToDelete.name}</p>
              {contactToDelete.position && (
                <p className="text-xs text-muted-foreground mt-1">{contactToDelete.position}</p>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-contact-cancel">{language === 'ar' ? 'إلغاء' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => contactToDelete && deleteContact(contactToDelete._id)}
              data-testid="delete-contact-confirm"
            >
              {language === 'ar' ? 'حذف' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
